import { useCallback, useMemo, useState } from 'react'
import {
  useReactTable,
  getPaginationRowModel,
  getCoreRowModel,
  getFilteredRowModel,
} from '@tanstack/react-table'
import * as XLSX from 'xlsx'
import { reportService } from '@/app/services/reportService'
import { TableReportReferralColumnsDefinition } from './components/TableReportReferralColumnsDefinition'
import { useReportRefferal } from '@/app/hooks/useReportReferral'
import {
  ReportReferral,
  ReportReferralParams,
} from '@/app/services/reportService/reportReferral'
import { format } from 'date-fns'
import { FiltersType } from '../components/FilterReports'
import { useAuth } from '@/app/hooks/useAuth'
import { fetchAllReportData } from '@/app/utils/fetchAllReportData'
import { useToast } from '@/view/components/ui/use-toast'

const AMOUNT_REPORTS_FETCH = Number(import.meta.env.VITE_AMOUNT_REPORTS_FETCH)

export const useReportReferralController = () => {
  const [exportProgress, setExportProgress] = useState(0)
  const [isExporting, setIsExporting] = useState(false)
  const { data, isFetching, isLoading, setParams, params } = useReportRefferal()
  const { isLeader, isSeller } = useAuth()
  const { toast } = useToast()

  const { columns } = TableReportReferralColumnsDefinition()

  const table = useReactTable({
    data: data?.referrals || [],
    columns,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      pagination: {
        pageIndex: 0,
        pageSize: data?.pagination.limit || 100,
      },
    },
  })

  const fetchAllData = useCallback(async (): Promise<ReportReferral[]> => {
    if (!data) return []

    setIsExporting(true)
    setExportProgress(0)

    try {
      return await fetchAllReportData<ReportReferral>({
        total: data.pagination.total,
        batchSize: AMOUNT_REPORTS_FETCH,
        onProgress: setExportProgress,
        fetchBatch: async ({ skip, limit }) => {
          const exportParams: ReportReferralParams = { ...params, limit, skip }
          const batchData = await reportService.reportReferrals(exportParams)
          return batchData.referrals
        },
      })
    } finally {
      setIsExporting(false)
    }
  }, [data, params])

  const handleExportToExcel = useCallback(async () => {
    try {
      const fetchedData = await fetchAllData()

      const formattedData = fetchedData.map((referral) => ({
        cpf: referral.cpf || '',
        name: referral.name || '',
        email: referral.email || '',
        phone: referral.phone || 0,
        registered: referral.registered ? 'SIM' : 'NÃO',
        date: format(referral.createdAt, 'dd/MM/yyyy, HH:mm') || '',
      }))

      if (formattedData.length > 0) {
        const workbook = XLSX.utils.book_new()
        const worksheet = XLSX.utils.json_to_sheet(formattedData)
        XLSX.utils.book_append_sheet(workbook, worksheet, 'ExportedReport')
        XLSX.utils.sheet_add_aoa(worksheet, [
          ['Cpf', 'Nome', 'email', 'Telefone', 'Cadastrado?', 'Indicado em'],
        ])
        XLSX.writeFile(workbook, 'referral-reports.xlsx')
      }
    } catch (error) {
      console.error('Error exporting referral report:', error)
      toast({
        title: 'Não foi possível exportar o relatório',
        description:
          'Alguns dados não puderam ser carregados. Tente novamente.',
        variant: 'destructive',
      })
    } finally {
      setIsExporting(false)
    }
  }, [fetchAllData, toast])

  const getPageIndex = () => {
    if (!data) return 0
    return Math.floor(data.pagination.skip / data.pagination.limit) + 1
  }

  const getPageCount = () => {
    if (!data) return 0
    return Math.ceil(data.pagination.total / data.pagination.limit)
  }

  const handleNextPage = () => {
    if (!data) return
    const newPage = data.pagination.skip / data.pagination.limit + 1
    if (getPageCount() === getPageIndex()) return
    const skipValue = newPage * data.pagination.limit
    setParams((p) => ({ ...p, skip: skipValue }))
  }

  const handlePreviousPage = () => {
    if (!data) return
    const newPage = data.pagination.skip / data.pagination.limit - 1
    if (newPage < 0) return
    const skipValue = newPage * data.pagination.limit
    setParams((p) => ({ ...p, skip: skipValue }))
  }

  const filters: FiltersType[] = useMemo(() => {
    const result: FiltersType[] = ['Date']

    if (!isSeller) result.push('Sellers')
    if (!isLeader) result.push('Teams')

    return result
  }, [isLeader, isSeller])
  return {
    table,
    filters,
    handleNextPage,
    handlePreviousPage,
    getPageIndex,
    getPageCount,
    isFetching,
    isLoading,
    setParams,
    isExporting,
    exportProgress,
    handleExportToExcel,
  }
}
